/* eslint-disable react/prop-types */
import { ConditionChangeCallback } from '@components/Flows/Overlays/TaskConditionsDialog/types';
import {
	Autocomplete,
	Collapse,
	ListItem,
	ListItemText,
	TextField
} from '@mui/material';
import { FieldProps } from 'formik';
import React, { useState } from 'react';
import { withAutcompleteFormik } from './withFormik/withAutocompleteFormik';

interface WithConditionChange {
	onConditionChange?: ConditionChangeCallback;
}

type Props = React.ComponentProps<typeof Autocomplete> &
	React.ComponentProps<typeof TextField> &
	FieldProps &
	WithConditionChange;

interface IEncrichedOption {
	label?: string;
	value?: string;
	description?: string;
}

interface IOptionListItem extends React.HTMLAttributes<HTMLLIElement> {
	option: IEncrichedOption;
}

const AutocompleteListItem = ({
	option,
	onMouseOver,
	...rest
}: IOptionListItem) => {
	const [showDescription, setShowDescription] = useState(false);
	if (!option.description) {
		return <span {...rest}>{option?.label}</span>;
	}
	return (
		<ListItem
			{...rest}
			onMouseOver={(e) => {
				if (onMouseOver) {
					onMouseOver(e);
				}
				setShowDescription(true);
			}}
			onMouseOut={() => setShowDescription(false)}
			divider
		>
			<ListItemText
				primary={option.label}
				secondary={
					option.description && (
						<Collapse in={showDescription} timeout={{ enter: 700, exit: 700 }}>
							{intl.tr(option.description)}
						</Collapse>
					)
				}
				secondaryTypographyProps={{
					component: 'span'
				}}
			/>
		</ListItem>
	);
};

const FormikAutocompleteField = ({
	required,
	label,
	form,
	value,
	multiple,
	onConditionChange,
	...rest
}: Props) => {
	let readyVal = Array.isArray(value) ? null : value || null;
	if (multiple) {
		readyVal = Array.isArray(value) ? value : [];
	}
	return (
		<Autocomplete
			{...rest}
			multiple={multiple}
			getOptionLabel={(opt: any) => opt?.value || opt}
			filterSelectedOptions={multiple}
			value={readyVal}
			onChange={(_, option: unknown) => {
				if (multiple) {
					const values = (option as any[]).map((o) => o.value || o);
					if (onConditionChange) {
						onConditionChange(values as any);
					}
					form.setFieldValue(rest.name!, values);
				} else {
					const { value: v } = option as { value: string };
					if (onConditionChange) {
						onConditionChange(v as any);
					}
					form.setFieldValue(rest.name!, v);
				}
			}}
			renderOption={(props, option: any) => (
				<AutocompleteListItem option={option} {...props} />
			)}
			renderInput={(params) => (
				<TextField
					{...params}
					inputProps={{
						...params.inputProps,
						required: multiple ? (value as any[])?.length === 0 : null
					}}
					helperText={rest.error ? rest.error : ''}
					error={!!rest.error}
					required={required}
					label={label}
				/>
			)}
			filterOptions={(options, params) => {
				const { inputValue } = params;
				const val = inputValue.toLowerCase();
				if (Array.isArray(options))
					return options.filter(
						(option) =>
							option?.label?.toLowerCase().includes(val) ||
							option?.value?.toLowerCase().includes(val)
					);
				return [];
			}}
		/>
	);
};

FormikAutocompleteField.defaultProps = {
	onConditionChange: () => {}
};

export default withAutcompleteFormik(FormikAutocompleteField);
